// Shared extraction of the backend's structured `invalid_argument` details.
//
// Lives in its own module so BOTH the jam beacon (connector-upload-isolation.ts,
// the WEDGE-visibility path) AND the quarantine telemetry (connector-quarantine.ts,
// the DRAIN path) can read the rejected field path(s) from the SAME helper —
// without a circular import (isolation already imports quarantine for
// quarantineUnsyncableCount, so quarantine importing back into isolation would
// cycle) and without copy-pasting a no-PII-sensitive extraction in two places.
//
// WHY BOTH PATHS NEED IT (#751 + #799). #799 added field-level visibility to the
// `invalid_argument` jam beacon — but only on the WEDGE path. #751 makes a
// `capture_items` `invalid_argument` DRAIN (quarantine) instead of wedging, so
// that beacon no longer fires for the count path; the diagnostic would be lost.
// Threading the same `invalidFields` into the quarantine telemetry keeps "rejected
// on <field>" visible on the drain path, so #751 fixes the wedge WITHOUT a
// regression in #799's observability.

import { ApiError } from '../api'

/**
 * The validation field path(s) the backend rejected, when `err` is an Encore
 * `invalid_argument` carrying structured `details.invalidFields` (populated by
 * the backend `mapZodToInvalidArgument` → `zodIssuePaths`). These are Zod schema
 * KEYS — field names like `quantity` / `syncVersion` — NEVER captured values, so
 * they are safe for the no-PII jam beacon (and harmless in the quarantine
 * telemetry, which already carries the payload). Returns undefined when absent or
 * malformed (any non-validation error), so the caller simply omits the key. Reads
 * off the raw `ApiError.body` (`{ code, message, details }`) the same way
 * `handleLwwConflict` reads `body.details.authoritative`.
 */
export function invalidFieldsFrom(err: unknown): string[] | undefined {
  if (!(err instanceof ApiError)) return undefined
  const body = err.body
  const details =
    typeof body === 'object' && body !== null
      ? (body as Record<string, unknown>)['details']
      : null
  const fields =
    typeof details === 'object' && details !== null
      ? (details as Record<string, unknown>)['invalidFields']
      : null
  if (!Array.isArray(fields)) return undefined
  const strings = fields.filter((f): f is string => typeof f === 'string')
  return strings.length > 0 ? strings : undefined
}
